import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, Container } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { CONSULTANT_VIEW } from "@platform/feature-flags";
import {
  DashboardContainer,
  Footer,
  Loader,
  PortalHeader,
} from "@platform/portal-ui";
import { useUser } from "@platform/user/client";
import { useEffect, useMemo } from "react";
import {
  matchPath,
  Navigate,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router";

import {
  exitBrokerImpersonation,
  isImpersonatingBroker,
} from "../auth/auth0Pkce";
import { OPEN_PAGES, PREV_URL, ROUTE_NAMES } from "../constants";
import {
  useAuth,
  useNewVersionPrompt,
  useToastNotificationService,
} from "../hooks";
import { isProduction } from "../utils/environment";
import { clearUser } from "../utils/tracking";
import envVariables from "../projectEnvVariables";

// The pages a signed-out user may stay on; any other path sends them to login.
const SIGNED_OUT_PAGES = [
  "/",
  `/${ROUTE_NAMES.LOGIN}/:connection`,
  "/centro/callback",
  ...OPEN_PAGES.map((page) => `/${page}`),
];

const isSignedOutPage = (pathname) =>
  SIGNED_OUT_PAGES.some((pattern) => matchPath(pattern, pathname));

function useRouteMatch(patterns) {
  const { pathname } = useLocation();
  const basename = `/${pathname.split("/")[1]}`;
  const possibleMatch = patterns.find((pattern) =>
    matchPath(pattern, basename),
  );
  if (possibleMatch) return possibleMatch;

  return null;
}

function useMatchPage(pathname) {
  const matchPage = useMemo(
    () =>
      [
        `/${ROUTE_NAMES.CLIENTS}`,
        `/${ROUTE_NAMES.CLIENTS}/:id/*`,
        `/${ROUTE_NAMES.RFPS}/:id/*`,
        `/${ROUTE_NAMES.RFP}`,
        `/${ROUTE_NAMES.USERS}/:id`,
        `/${ROUTE_NAMES.DASHBOARD}`,
        `/${ROUTE_NAMES.CLIENT_SELECTION}`,
        `/${ROUTE_NAMES.ONBOARDING_ERROR}`,
        `/${ROUTE_NAMES.RESET_PASSWORD}`,
        `/${ROUTE_NAMES.DOCUMENT_CHECKLIST}`,
        `/${ROUTE_NAMES.ANALYSTS}`,
        `/${ROUTE_NAMES.MP_CONFIGS}`,
        `/${ROUTE_NAMES.MP_CONFIGS}/:id`,
        `/${ROUTE_NAMES.CARRIERS_REP_DIRECTORY}`,
        `/${ROUTE_NAMES.BBE}`,
        `/${ROUTE_NAMES.BBE}/:id/*`,
        `/${ROUTE_NAMES.CONSULTING}`,
        `/${ROUTE_NAMES.CONSULTING}/:id/*`,

        `/`,
      ].find((url) => matchPath(url, pathname)),
    [pathname],
  );
  return Boolean(matchPage);
}

function PortalContainer({ children }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const signedInUser = user.status === "signedIn" ? user : undefined;
  const { logOut, isAuthenticated, loading: authLoading } = useAuth();
  const { pathname, search } = useLocation();
  const consultantViewEnabled = useFeatureIsOn(CONSULTANT_VIEW);
  const { createToastNotification } = useToastNotificationService();
  useNewVersionPrompt({
    isEnabled: isAuthenticated && isProduction(),
  });

  useEffect(() => {
    if (
      !authLoading &&
      isAuthenticated &&
      signedInUser?.isConsultant &&
      consultantViewEnabled &&
      pathname === "/"
    ) {
      navigate(`/${ROUTE_NAMES.CONSULTING}`, { replace: true });
    }
  }, [
    authLoading,
    consultantViewEnabled,
    isAuthenticated,
    signedInUser?.isConsultant,
    navigate,
    pathname,
  ]);

  const handleLogout = async () => {
    try {
      await logOut();
      clearUser();
      sessionStorage.clear();
    } catch (err) {
      logger.error("Logout error", err);
    }
  };

  const handleExitBrokerView = async () => {
    try {
      await exitBrokerImpersonation();
    } catch (err) {
      logger.error("Exit broker view failed", err);
      createToastNotification({
        severity: "error",
        message: "Couldn't exit the broker view. Please try again.",
      });
    }
  };

  const isDashboard = useMatch("/") && isAuthenticated;
  const isLogin = useRouteMatch(["/", ROUTE_NAMES.LOGIN]) && !isAuthenticated;
  const isOnboardingError = useMatch("/onboarding/error") && !isAuthenticated;
  const isWorkbenchBuild2 = pathname.includes(
    `/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}/`,
  );
  const isCarriersRepDirectory = Boolean(
    useMatch(`/${ROUTE_NAMES.CARRIERS_REP_DIRECTORY}`),
  );
  const matchPage = useMatchPage(pathname);

  // Redirects in the same render the session ends, so a signed-out user never sees the
  // page they were on, even for one render.
  const mustSignIn =
    !authLoading && !isAuthenticated && !isSignedOutPage(pathname);
  if (mustSignIn) {
    return (
      <Navigate to="/" replace state={{ [PREV_URL]: `${pathname}${search}` }} />
    );
  }

  const renderBody = () => {
    if (authLoading && !isLogin) {
      return <Loader />;
    }
    return matchPage ? (
      children
    ) : (
      <Container>
        <Box mb={5} mt={18}>
          {children}
        </Box>
      </Container>
    );
  };

  const renderAppContent = () => (
    <>
      {isAuthenticated ? (
        <PortalHeader
          appearance={isDashboard ? "dashboard" : "default"}
          currentPath={pathname}
          user={{
            givenName: signedInUser?.givenName,
            familyName: signedInUser?.familyName,
            userType: signedInUser?.role,
            isAdmin: signedInUser?.isAdmin,
            isAgent: signedInUser?.isAgent,
          }}
          onLogout={handleLogout}
          onExitBrokerView={
            isImpersonatingBroker() ? handleExitBrokerView : undefined
          }
          helpEmail={envVariables.VITE_REACT_APP_NEED_HELP_EMAIL}
          teamEmail={envVariables.VITE_REACT_APP_NEED_TEAM_EMAIL}
        />
      ) : null}
      {renderBody()}
    </>
  );

  return (
    <>
      {isAuthenticated && isDashboard ? (
        <DashboardContainer>{renderAppContent()}</DashboardContainer>
      ) : (
        renderAppContent()
      )}
      {/* FIXME: There is a layout issue with this entire page, hiding this for now */}
      {!isLogin &&
        !isOnboardingError &&
        !isWorkbenchBuild2 &&
        !isCarriersRepDirectory && (
          <Footer
            privacyPolicyUrl={envVariables.VITE_REACT_APP_PRIVACY_POLICY_URL}
          />
        )}
    </>
  );
}

export default PortalContainer;
